import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import viewEyeIcon from "../../assets/eye.svg";
import Ambulanceicon from "../../assets/Ambulanceicon.svg";
import nodata from "../../assets/nodata.svg";
import OrderDetailsModal, {
  OrderDetailsSection,
} from "../shared/OrderDetailsModal";
import { getHospitalDetails } from "../../utils/hospitalStorage";
import { APP_ROUTES } from "../../constants/approutes";

const trackingTimelineStyles = `
@keyframes ambulanceTimelineIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.ambulance-timeline-item {
  animation: ambulanceTimelineIn 0.45s ease-out both;
}
`;

const COMMAND_CENTER_URL = process.env.REACT_APP_API_COMMAND_CENTER_URL;

const resolveHospitalId = () => {
  const fromStorage = localStorage.getItem("hospitalid") || "";
  if (fromStorage) return fromStorage;

  const details = getHospitalDetails();
  return (
    details?.userId ||
    details?.hospitalId ||
    details?.hospitalCode ||
    details?.id ||
    ""
  );
};

const normalizeAmbulanceList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.records)) return payload.records;
  return [];
};

const getFirstTicket = (order) =>
  Array.isArray(order?.enquiryTickets) ? order.enquiryTickets[0] : null;

const getOrderId = (order) =>
  order?.orderId || order?.id || getFirstTicket(order)?.ticketId || "";

const getPatientName = (order) => order?.patientName || "—";

const getMobile = (order) =>
  order?.patientMobile || order?.patientMobileNo || order?.mobileNumber || "—";

const getBookingStatus = (order) =>
  order?.bookingStatus || getFirstTicket(order)?.ticketStatus || "";

const getVendorStatus = (order) => order?.vendorStatus || "";

const getDisplayStatus = (order) =>
  getVendorStatus(order) || getBookingStatus(order) || "";

const getBookingTime = (order) => {
  const value = order?.bookingTime || order?.createdDate;
  return value ? formatTrackingDate(value) : "—";
};

const getEventDate = (entry) =>
  entry?.statusDate ||
  entry?.updatedAt ||
  entry?.modifiedDate ||
  entry?.createdDate ||
  "";

const parseEventDate = (value) => {
  if (!value) return null;
  // Command center format: "dd-MM-yyyy hh:mm AM/PM"
  const match = String(value).match(
    /^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?$/i
  );
  if (match) {
    const [, dd, MM, yyyy, hh, min, ap] = match;
    let hours = parseInt(hh, 10);
    if (ap) {
      const upper = ap.toUpperCase();
      if (upper === "PM" && hours < 12) hours += 12;
      if (upper === "AM" && hours === 12) hours = 0;
    }
    return new Date(+yyyy, +MM - 1, +dd, hours, +min);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTrackingDate = (value) => {
  if (!value) return "";
  const parsed = parseEventDate(value);
  if (!parsed) return value;
  return format(parsed, "dd MMM yyyy, hh:mm a");
};

const normalizeStatusKey = (status = "") =>
  String(status).trim().toLowerCase().replace(/_/g, " ");

const AMBULANCE_STATUS_META = {
  new: {
    iconType: "booked",
    iconBg: "bg-gradient-to-br from-sky-400 to-blue-500",
    iconRing: "ring-sky-100",
    cardBg: "from-sky-50 to-white",
    border: "border-sky-200",
    badge: "bg-sky-100 text-sky-800 border border-sky-200",
  },
  initiated: {
    iconType: "booked",
    iconBg: "bg-gradient-to-br from-violet-500 to-indigo-600",
    iconRing: "ring-violet-100",
    cardBg: "from-violet-50 to-white",
    border: "border-violet-200",
    badge: "bg-violet-100 text-violet-800 border border-violet-200",
  },
  pending: {
    iconType: "booked",
    iconBg: "bg-gradient-to-br from-slate-400 to-slate-600",
    iconRing: "ring-slate-100",
    cardBg: "from-slate-50 to-white",
    border: "border-slate-200",
    badge: "bg-slate-100 text-slate-700 border border-slate-200",
  },
  "request in progress": {
    iconType: "assigned",
    iconBg: "bg-gradient-to-br from-amber-400 to-orange-500",
    iconRing: "ring-amber-100",
    cardBg: "from-amber-50 to-white",
    border: "border-amber-200",
    badge: "bg-amber-100 text-amber-800 border border-amber-200",
  },
  inprogress: {
    iconType: "assigned",
    iconBg: "bg-gradient-to-br from-amber-400 to-orange-500",
    iconRing: "ring-amber-100",
    cardBg: "from-amber-50 to-white",
    border: "border-amber-200",
    badge: "bg-amber-100 text-amber-800 border border-amber-200",
  },
  confirmed: {
    iconType: "assigned",
    iconBg: "bg-gradient-to-br from-teal-500 to-cyan-600",
    iconRing: "ring-teal-100",
    cardBg: "from-teal-50 to-white",
    border: "border-teal-200",
    badge: "bg-teal-100 text-teal-800 border border-teal-200",
  },
  assigned: {
    iconType: "assigned",
    iconBg: "bg-gradient-to-br from-blue-500 to-[#004276]",
    iconRing: "ring-blue-100",
    cardBg: "from-blue-50 to-white",
    border: "border-blue-200",
    badge: "bg-blue-100 text-blue-800 border border-blue-200",
  },
  "ambulance assigned": {
    iconType: "assigned",
    iconBg: "bg-gradient-to-br from-blue-500 to-[#004276]",
    iconRing: "ring-blue-100",
    cardBg: "from-blue-50 to-white",
    border: "border-blue-200",
    badge: "bg-blue-100 text-blue-800 border border-blue-200",
  },
  dispatched: {
    iconType: "enroute",
    iconBg: "bg-gradient-to-br from-orange-500 to-amber-600",
    iconRing: "ring-orange-100",
    cardBg: "from-orange-50 to-white",
    border: "border-orange-200",
    badge: "bg-orange-100 text-orange-800 border border-orange-200",
  },
  "ambulance en route": {
    iconType: "enroute",
    iconBg: "bg-gradient-to-br from-[#FF6B6B] to-[#C62828]",
    iconRing: "ring-red-100",
    cardBg: "from-orange-50 to-white",
    border: "border-orange-200",
    badge: "bg-orange-100 text-orange-800 border border-orange-200",
  },
  "en route": {
    iconType: "enroute",
    iconBg: "bg-gradient-to-br from-[#FF6B6B] to-[#C62828]",
    iconRing: "ring-red-100",
    cardBg: "from-orange-50 to-white",
    border: "border-orange-200",
    badge: "bg-orange-100 text-orange-800 border border-orange-200",
  },
  completed: {
    iconType: "completed",
    iconBg: "bg-gradient-to-br from-emerald-500 to-[#00BD7F]",
    iconRing: "ring-emerald-100",
    cardBg: "from-emerald-50 to-white",
    border: "border-emerald-200",
    badge: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  },
  "trip completed": {
    iconType: "completed",
    iconBg: "bg-gradient-to-br from-emerald-500 to-[#00BD7F]",
    iconRing: "ring-emerald-100",
    cardBg: "from-emerald-50 to-white",
    border: "border-emerald-200",
    badge: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  },
  closed: {
    iconType: "completed",
    iconBg: "bg-gradient-to-br from-emerald-500 to-[#00BD7F]",
    iconRing: "ring-emerald-100",
    cardBg: "from-emerald-50 to-white",
    border: "border-emerald-200",
    badge: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  },
  cancelled: {
    iconType: "cancelled",
    iconBg: "bg-gradient-to-br from-red-500 to-rose-600",
    iconRing: "ring-red-100",
    cardBg: "from-red-50 to-white",
    border: "border-red-200",
    badge: "bg-red-100 text-red-800 border border-red-200",
  },
  rejected: {
    iconType: "cancelled",
    iconBg: "bg-gradient-to-br from-red-500 to-rose-600",
    iconRing: "ring-red-100",
    cardBg: "from-red-50 to-white",
    border: "border-red-200",
    badge: "bg-red-100 text-red-800 border border-red-200",
  },
  failed: {
    iconType: "cancelled",
    iconBg: "bg-gradient-to-br from-red-500 to-rose-600",
    iconRing: "ring-red-100",
    cardBg: "from-red-50 to-white",
    border: "border-red-200",
    badge: "bg-red-100 text-red-800 border border-red-200",
  },
};

const getTrackingStatusMeta = (status = "") => {
  const key = normalizeStatusKey(status);
  if (AMBULANCE_STATUS_META[key]) {
    return AMBULANCE_STATUS_META[key];
  }

  const value = key;

  if (/cancel|reject|fail|declin/.test(value)) {
    return AMBULANCE_STATUS_META.cancelled;
  }

  if (/complete|deliver|close|finish|done|drop|arriv|reached|trip/.test(value)) {
    return AMBULANCE_STATUS_META.completed;
  }

  if (/dispatch|en.?route|on.?way|transit|travel|pick|started|moving/.test(value)) {
    return AMBULANCE_STATUS_META["ambulance en route"];
  }

  if (/assign|confirm|accept|allocated|driver|progress/.test(value)) {
    return AMBULANCE_STATUS_META.assigned;
  }

  if (/initiat|book|new|open/.test(value)) {
    return AMBULANCE_STATUS_META.initiated;
  }

  return AMBULANCE_STATUS_META.pending;
};

const StatusBadge = ({ status, className = "" }) => {
  if (!status) return null;
  const meta = getTrackingStatusMeta(status);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${meta.badge} ${className}`}
    >
      {status}
    </span>
  );
};

const TimelineIcon = ({ type, className = "w-5 h-5" }) => {
  if (type === "cancelled") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M9 9l6 6M15 9l-6 6" />
      </svg>
    );
  }

  if (type === "completed") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    );
  }

  if (type === "enroute") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 12h2l2.5-7 3 14 2.5-5H21" />
        <rect x="2" y="14" width="16" height="6" rx="1" />
      </svg>
    );
  }

  if (type === "assigned") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    );
  }

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
};

export function AmbulanceTrackingTimeline({ events = [] }) {
  const sortedEvents = [...events].sort(
    (a, b) =>
      (parseEventDate(getEventDate(a))?.getTime() || 0) -
      (parseEventDate(getEventDate(b))?.getTime() || 0)
  );

  if (sortedEvents.length === 0) {
    return (
      <p className="text-sm text-slate-500 mb-0">No tracking history available.</p>
    );
  }

  return (
    <>
      <style>{trackingTimelineStyles}</style>
      <div className="rounded-2xl border border-slate-100 bg-gradient-to-br from-[#F8FAFC] to-white p-4 sm:p-5">
        <div className="relative">
          <div className="absolute left-[21px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-[#FF6B6B] via-[#00BD7F] to-slate-200" />

          <div className="space-y-0">
            {sortedEvents.map((entry, index) => {
              const meta = getTrackingStatusMeta(
                entry.status || entry.statusName || ""
              );
              const isLatest = index === sortedEvents.length - 1;
              const eventDate = getEventDate(entry);
              const statusLabel =
                entry.status || entry.statusName || "Status update";

              return (
                <div
                  key={`${statusLabel}-${index}`}
                  className="ambulance-timeline-item relative flex gap-4 pb-8 last:pb-0"
                  style={{ animationDelay: `${index * 90}ms` }}
                >
                  <div className="relative z-10 flex-shrink-0">
                    {isLatest && (
                      <span className="absolute inset-0 rounded-full bg-[#00BD7F]/35 animate-ping" />
                    )}
                    <div
                      className={`relative w-11 h-11 rounded-full flex items-center justify-center text-white shadow-lg ring-4 ${meta.iconBg} ${meta.iconRing} ${
                        isLatest ? "animate-[pulse_2s_ease-in-out_infinite]" : ""
                      }`}
                    >
                      <TimelineIcon type={meta.iconType} />
                    </div>
                  </div>

                  <div
                    className={`flex-1 rounded-xl border ${meta.border} bg-gradient-to-r ${meta.cardBg} px-4 py-3 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${meta.badge}`}
                      >
                        {isLatest && (
                          <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                        )}
                        {statusLabel}
                      </span>
                      {eventDate && (
                        <span className="text-xs text-slate-500 font-medium">
                          {formatTrackingDate(eventDate)}
                        </span>
                      )}
                    </div>
                    {isLatest && (
                      <p className="text-[11px] font-semibold text-[#00BD7F] mt-2 mb-0 flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00BD7F] animate-pulse" />
                        Latest update
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

const buildAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  const tenantId = localStorage.getItem("tenantId") || "";
  return {
    Accept: "*/*",
    "Content-Type": "application/json",

  };
};

export default function AmbulanceOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchAmbulanceOrders();
  }, []);

  const fetchAmbulanceOrders = async () => {
    const token = localStorage.getItem("auth_token");
    const tenantId = localStorage.getItem("tenantId");
    const clinicId = localStorage.getItem("clinicId");

    setLoading(true);
    setFetchError("");

    if (!token) {
      setFetchError("Authentication token not found. Please log in again.");
      setOrders([]);
      setLoading(false);
      return;
    }

    try {
      const today = new Date().toISOString().split("T")[0];
      const resp = await fetch(
        `${API_URL}orders/get-all?serviceType=AMBULANCE&clinicId=${clinicId}` +
          `&fromDate=${today}&toDate=${today}`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            tenantId: tenantId,
          },
        }
      );

      if (!resp.ok) {
        throw new Error(`Failed to fetch ambulance list (${resp.status})`);
      }

      const data = await resp.json();
      setOrders(data?.data?.content || data?.records || []);
    } catch (err) {
      console.error("Ambulance orders fetch error:", err);
      setFetchError(err?.message || "Failed to load ambulance requests.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedOrder(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#C62828] rounded-full flex items-center justify-center">
            <img src={Ambulanceicon} alt="Ambulance" className="w-6 h-6 object-contain" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#004276] mb-0">
              Ambulance{" "}
              <span className="bg-[#00BD7F] text-white font-medium px-2 py-1 rounded-md text-sm ml-1">
                {orders.length}
              </span>
            </h2>
            <p className="text-xs text-[#C62828] mb-0 mt-1 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF3D42] shrink-0" />
              Available 24/7
            </p>
          </div>
        </div>
        <button
          type="button"
          className="text-[#00BD7F] text-sm font-semibold hover:underline -mt-2"
          onClick={() => navigate(APP_ROUTES.CLINICAMBULANCEDETAILS)}
        >
          View All
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-[0px_4px_8px_rgba(0,0,0,0.15)] flex-1 flex flex-col">
        {loading ? (
          <div
            className="flex flex-col items-center justify-center text-gray-500 py-8"
            style={{ height: "360px" }}
          >
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B6B]" />
            <p className="mt-2 mb-0">Loading ambulance requests...</p>
          </div>
        ) : orders.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-gray-500 py-8"
            style={{ height: "360px" }}
          >
            <img src={nodata} alt="No Data" className="w-48 h-48 mb-4" />
            <div>{fetchError || "No Ambulance requests available"}</div>
          </div>
        ) : (
          <div style={{ height: "360px", overflowY: "auto" }}>
            {orders.map((order, index) => {
              const orderId = getOrderId(order);
              const bookingStatus = getBookingStatus(order);
              const vendorStatus = getVendorStatus(order);
              return (
                <div
                  key={`${orderId}-${index}`}
                  className="flex items-center justify-between px-5 py-3 border-b last:border-none"
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-base text-[#1A1A1A] mb-1 truncate">
                      {getPatientName(order)}
                    </p>
                    <p className="text-sm text-[#7D8592] mb-0">
                  
                      Phone:{" "}
                      <span className="font-semibold text-[#1A1A1A]">
                        {getMobile(order)}
                      </span>
                    </p>
                    <p className="text-sm text-[#7D8592] mb-0 mt-1">
                      Booked:{" "}
                      <span className="font-semibold text-[#1A1A1A]">
                        {getBookingTime(order)}
                      </span>
                    </p>
                    {(bookingStatus || vendorStatus) && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-1.5">
                        
                        {vendorStatus && (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-xs text-[#7D8592]">Vendor</span>
                            <StatusBadge status={vendorStatus} />
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="bg-white border-none outline-none p-2 rounded-full flex items-center justify-center flex-shrink-0"
                    onClick={() => handleViewDetails(order)}
                  >
                    <img src={viewEyeIcon} alt="View" className="w-6 h-6" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isModalVisible && selectedOrder && (
        <OrderDetailsModal
          open={isModalVisible}
          onClose={closeModal}
          subtitle="Ambulance Request Details"
          patientName={getPatientName(selectedOrder)}
          status={getDisplayStatus(selectedOrder)}
          address={selectedOrder.pickupAddress || ""}
          addressLabel="Pickup Address"
          showAddress={Boolean(selectedOrder.pickupAddress)}
          infoFields={[
            { label: "Order ID", value: getOrderId(selectedOrder), mono: true },
            { label: "Booking Time", value: getBookingTime(selectedOrder) },
            { label: "Mobile No", value: getMobile(selectedOrder) },
            { label: "Booking Status", value: getBookingStatus(selectedOrder) },
            { label: "Ticket Id", value: selectedOrder.enquiryTickets?.[0]?.ticketId },
            { label: "Reason", value: selectedOrder.ambulaceRequestReason },
      
          ]}
        >
          {selectedOrder.dropAddress && (
            <div className="rounded-xl border border-[#C5E8E3] bg-gradient-to-r from-[#E8F6F4] to-white px-4 py-4">
              <p className="text-[11px] font-medium uppercase tracking-wide text-[#0D6B6E] mb-1">
                Drop Address
              </p>
              <p className="text-sm font-medium text-slate-800 mb-0 leading-relaxed break-words">
                {selectedOrder.dropAddress}
              </p>
            </div>
          )}

         
        </OrderDetailsModal>
      )}
    </div>
  );
}
